import { InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';

/** In-session views the shared UI can ask its host to show. */
export type MapsView = 'search' | 'admin-dashboard' | 'admin-configuration' | 'admin-partners';

/**
 * Full location space. The pre-session states ('access', 'no-access') exist
 * only in the standalone host, which owns real routes and guards; the
 * MJExplorer host never emits them (an unauthenticated surface renders its own
 * empty state instead).
 */
export type MapsLocation = MapsView | 'access' | 'no-access';

/**
 * The single seam between the shared MAPS UI and whatever hosts it. The
 * standalone app implements it over the Angular Router; the MJExplorer surface
 * implements it over local view state inside a routerless dashboard. GraphQL
 * transport is deliberately NOT part of this contract — each host constructs
 * its own Apollo client (cookie vs bearer) and the shared services inject the
 * default `Apollo` instance.
 */
export interface MapsHostAdapter {
    /** Emits the current location on subscribe and again on every change. */
    readonly Location$: Observable<MapsLocation>;
    readonly CurrentLocation: MapsLocation;
    /**
     * Whether the header should offer a way back into the embedding host's own
     * UI without ending the session. False on standalone (there is no host
     * shell) and for app-scoped guests (magic-link partners have nowhere else
     * to go); true for staff sessions inside MJExplorer.
     */
    readonly CanReturnToHost: boolean;
    /** Leave the MAPS surface for the host shell, keeping the session alive. */
    ReturnToHost(): void;
    /** Show an in-session view. */
    NavigateTo(view: MapsView): void;
    /**
     * Absolute origin of the MAPS API host (e.g.
     * 'https://agu-stage-app-mjapi.azurewebsites.net'). The Partner Links
     * screen composes magic-link redeem URLs from it
     * (`<origin>/magic-link/redeem?token=…`) — the API serves the redeem
     * route, so the UI's own origin is the wrong base.
     */
    readonly ApiOrigin: string;
    /** End the session and leave the app (host decides where "out" is). */
    SignOut(): Promise<void>;
}

export const MAPS_HOST_ADAPTER = new InjectionToken<MapsHostAdapter>('MapsHostAdapter');
